"use client";

import { getSettings } from "@/components/settings";
import { isInfrontReady } from "@/services/infront/utils";
import { loadScripts } from "@/utils/scripts";
import {
  createContext,
  PropsWithChildren,
  useContext,
  useEffect,
  useState,
} from "react";

const INFRONT_DEPENDENCIES = [
  {
    name: "core",
    id: "infront-core",
    src: "https://wtk.infrontservices.com/js/CommonFramework-2.11.19.min.js",
    priority: 1,
  },
  {
    name: "core",
    id: "infront-ui",
    src: "https://wtk.infrontservices.com/js/InfrontUI-3.11.19.min.js",
    priority: 2,
  },
  {
    name: "core",
    id: "infront-locale",
    src: "https://wtk.infrontservices.com/languages/sv.js",
    priority: 3,
  },
  {
    name: "hollowcandlestick",
    src: "https://code.highcharts.com/stock/9.3.1/modules/hollowcandlestick.js",
    priority: 3,
  },
  {
    name: "heikinashi",
    src: "https://code.highcharts.com/stock/9.3.1/modules/heikinashi.js",
    priority: 3,
  },
  {
    name: "highstock",
    src: "https://code.highcharts.com/stock/9.3.1/highstock.js",
    priority: 3,
  },
];

type InfrontContextType = {
  isReady: boolean;
  load?: (
    name: (
      | "core"
      | "locale"
      | "hollowcandlestick"
      | "heikinashi"
      | "highstock"
    )[],
  ) => Promise<void>;
};

export const InfrontContext = createContext<InfrontContextType>({
  isReady: false,
});
type InfrontDependencies = (
  | "core"
  | "locale"
  | "hollowcandlestick"
  | "heikinashi"
  | "highstock"
)[];
export const InfrontContextProvider = ({
  children,
  token,
}: PropsWithChildren<{ token?: string }>) => {
  const [isReady, setIsReady] = useState(false);
  const [loaded, setIsLoaded] = useState<InfrontDependencies>([]);

  const load = (names: InfrontDependencies): Promise<void> =>
    new Promise(async (resolve) => {
      await loadScripts(
        INFRONT_DEPENDENCIES.filter(
          (dep) => dep.priority == 1 && names.includes(dep.name as any),
        ),
      );
      await loadScripts(
        INFRONT_DEPENDENCIES.filter(
          (dep) => dep.priority == 2 && names.includes(dep.name as any),
        ),
      );
      await loadScripts(
        INFRONT_DEPENDENCIES.filter(
          (dep) => dep.priority == 3 && names.includes(dep.name as any),
        ),
      );
      setIsLoaded(names);
      resolve();
    });

  useEffect(() => {
    const init = async () => {
      await isInfrontReady();

      const settings = getSettings();

      window.infront = new window.Infront.UI({
        signed_token: `${token}`,
        streaming: !settings.disableAnimations,
        store_session: true,
        language: "sv",
      });

      window.infront.registerEventObserver("onReady", () => {
        setIsReady(true);
        const urls = [
          process.env.NEXT_PUBLIC_FRONTEND_URL + "/css/wtk/InfrontFramework-3.11.18.css",
          process.env.NEXT_PUBLIC_FRONTEND_URL + "/css/wtk/theme.css",
        ];
        urls.forEach((href) => {
          if (!document.querySelector(`link[href="${href}"]`)) {
            const link = document.createElement("link");
            link.rel = "stylesheet";
            link.href = href!;
            document.head.appendChild(link);
          }
        });
      });
      window.infront.init();
    };

    if (loaded.length > 0 && !isReady) init();
  }, [loaded]);

  return (
    <InfrontContext.Provider
      value={{
        isReady,
        load,
      }}
    >
      {children}
      </InfrontContext.Provider>
  );
};

export const useInfront = () => {
  const { isReady, load } = useContext(InfrontContext);

  return { isReady, load };
};
